import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { supervisorApi } from "@/lib/SupervisorApi";
import { Modal, Field, inputCls } from "./SupervisorPrimitives";

/**
 * Device-Trust escape hatch (Feb 2026).
 *
 * When a client site has no active browser session left (e.g. the
 * last logged-in machine was rebooted / cache-wiped), they can't
 * self-generate a device-approval code from Settings → Συσκευές
 * because that endpoint requires a logged-in operator. This dialog
 * bridges the gap: the supervisor generates the code from the
 * central admin panel and dictates it (or shows the QR) to the
 * customer over the phone. Redemption on the tenant side is
 * identical to the in-app flow.
 */
export default function NewDeviceCodeDialog({ tenant, onClose }) {
  const [labelHint, setLabelHint] = useState("");
  const [code, setCode] = useState(null); // { code, expires_at, ttl_minutes }
  const [busy, setBusy] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");

  const generate = async () => {
    setBusy(true);
    try {
      const r = await supervisorApi.generateDeviceCode(tenant.tenant_code, labelHint.trim() || null);
      setCode(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα δημιουργίας κωδικού");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const QR = await import("qrcode");
        const url = await QR.toDataURL(
          JSON.stringify({ code: code.code, label: labelHint || "" }),
          { width: 220, margin: 1 }
        );
        if (!cancelled) setQrDataUrl(url);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [code, labelHint]);

  return (
    <Modal title={`Νέα Συσκευή POS · ${tenant.tenant_code}`} onClose={onClose}>
      {!code ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-blue-900/30 border border-blue-800 px-4 py-3 text-xs text-blue-200 leading-relaxed">
            Δημιουργεί έναν 8-ψήφιο κωδικό μιας χρήσης για να εγκριθεί
            μία νέα συσκευή στο ταμείο του πελάτη. Δώσε τον κωδικό
            προφορικά ή δείξε το QR — ισχύει 30 λεπτά.
          </div>
          <Field label="Ετικέτα συσκευής (προαιρετικό)">
            <input
              type="text"
              value={labelHint}
              onChange={(e) => setLabelHint(e.target.value.slice(0, 40))}
              placeholder="π.χ. Ταμείο Α, Bar Terminal"
              maxLength={40}
              className={inputCls}
              data-testid="sup-new-device-label"
            />
          </Field>
          <div className="flex gap-2 justify-end pt-4 border-t border-zinc-800 -mx-6 px-6">
            <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold">Άκυρο</button>
            <button
              type="button"
              onClick={generate}
              disabled={busy}
              className="h-10 px-5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm disabled:opacity-40"
              data-testid="sup-new-device-generate"
            >
              {busy ? "..." : "Δημιουργία Κωδικού"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-bold mb-2">
              Πληκτρολόγησε στη νέα συσκευή
            </div>
            <div
              className="inline-block bg-emerald-950/50 border-2 border-emerald-500 rounded-lg py-4 px-6"
              data-testid="sup-new-device-code-display"
            >
              <div className="text-4xl font-mono font-bold tracking-[0.3em] text-emerald-100 tabular-nums">
                {code.code}
              </div>
            </div>
            <div className="mt-2">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(code.code);
                  toast.success("Αντιγράφηκε");
                }}
                className="text-xs text-emerald-400 hover:text-emerald-200 inline-flex items-center gap-1"
                data-testid="sup-new-device-copy"
              >
                <Copy size={12} /> Αντιγραφή
              </button>
            </div>
          </div>
          {qrDataUrl && (
            <div className="flex justify-center">
              {/* eslint-disable-next-line */}
              <img src={qrDataUrl} alt="QR κωδικός" className="rounded-md bg-white p-2" data-testid="sup-new-device-qr" />
            </div>
          )}
          <div className="text-[11px] text-center text-zinc-500">
            Ισχύει {code.ttl_minutes || 30} λεπτά · Μία χρήση
          </div>
          <div className="flex justify-center pt-3 border-t border-zinc-800 -mx-6 px-6">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm"
              data-testid="sup-new-device-done"
            >
              Έγινε
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
